/**
 * UN-DOLLAR RENTFLOW PORTAL — PERMISSION SYSTEM
 * 
 * Portal-specific role-based access control (RBAC)
 * Supports property owners, property managers, assistants, and maintenance staff
 */

/**
 * Portal Permission Definitions
 * Granular permissions for portal features
 */
export enum PortalPermission {
  // Property Management
  PROPERTY_VIEW = 'property:view',
  PROPERTY_CREATE = 'property:create',
  PROPERTY_UPDATE = 'property:update',
  PROPERTY_DELETE = 'property:delete',
  PROPERTY_TRANSFER = 'property:transfer',
  
  // Tenant Management
  TENANT_VIEW = 'tenant:view',
  TENANT_CREATE = 'tenant:create',
  TENANT_UPDATE = 'tenant:update',
  TENANT_DELETE = 'tenant:delete',
  TENANT_COMMUNICATE = 'tenant:communicate',
  
  // Lease Management
  LEASE_VIEW = 'lease:view',
  LEASE_CREATE = 'lease:create',
  LEASE_UPDATE = 'lease:update',
  LEASE_DELETE = 'lease:delete',
  LEASE_SIGN = 'lease:sign',
  LEASE_TERMINATE = 'lease:terminate',
  
  // Financial Management
  FINANCIAL_VIEW = 'financial:view',
  FINANCIAL_MANAGE = 'financial:manage',
  FINANCIAL_EXPORT = 'financial:export',
  PAYMENT_VIEW = 'payment:view',
  PAYMENT_PROCESS = 'payment:process',
  PAYMENT_REFUND = 'payment:refund',
  INVOICE_VIEW = 'invoice:view',
  INVOICE_CREATE = 'invoice:create',
  
  // Maintenance & Work Orders
  MAINTENANCE_VIEW = 'maintenance:view',
  MAINTENANCE_CREATE = 'maintenance:create',
  MAINTENANCE_UPDATE = 'maintenance:update',
  MAINTENANCE_ASSIGN = 'maintenance:assign',
  MAINTENANCE_COMPLETE = 'maintenance:complete',
  MAINTENANCE_DELETE = 'maintenance:delete',
  
  // Documents
  DOCUMENT_VIEW = 'document:view',
  DOCUMENT_UPLOAD = 'document:upload',
  DOCUMENT_DELETE = 'document:delete',
  DOCUMENT_SIGN = 'document:sign',
  
  // Reports & Analytics
  REPORT_VIEW = 'report:view',
  REPORT_GENERATE = 'report:generate',
  REPORT_EXPORT = 'report:export',
  ANALYTICS_VIEW = 'analytics:view',
  
  // Team Management
  TEAM_VIEW = 'team:view',
  TEAM_INVITE = 'team:invite',
  TEAM_MANAGE = 'team:manage',
  TEAM_REMOVE = 'team:remove',
  ROLE_ASSIGN = 'role:assign',
  
  // Settings & Configuration
  SETTINGS_VIEW = 'settings:view',
  SETTINGS_UPDATE = 'settings:update',
  BILLING_VIEW = 'billing:view',
  BILLING_MANAGE = 'billing:manage',
}

/**
 * Internal Admin Roles (for internal admins, NOT portal users)
 * These roles have full access to the admin/management features
 * DO NOT use these in portal user documents
 */
export type AdminRole = 
  | 'superadmin'          // Full system access
  | 'admin'               // Administrative access
  | 'sales'               // Sales and customer management
  | 'support'             // Customer support team
  | 'accountant';         // Financial/accounting access

/**
 * Portal User Roles (for property management portal)
 * Property owners, managers, tenants, and staff
 * These are the only roles allowed in portal user documents
 */
export type PortalRole = 
  | 'property_owner'      // Full access to owned properties
  | 'property_manager'    // Manages assigned properties (read-only financials)
  | 'assistant'           // Limited read/write (no financial access)
  | 'maintenance_coordinator' // Work orders only
  | 'tenant';             // Self-service portal (Phase 3)

/**
 * Union type for all possible user roles
 * Used in AuthContext to handle both admin and portal users
 */
export type UserRole = AdminRole | PortalRole;

/**
 * Portal Role Permission Mapping
 * Defines what permissions each PORTAL role has
 * 
 * Note: AdminRoles have their own permission system (not defined here)
 * Admin users in the portal get all permissions
 */
export const PORTAL_ROLE_PERMISSIONS: Record<PortalRole, PortalPermission[]> = {
  /**
   * PROPERTY OWNER - Full access to all owned properties
   */
  property_owner: Object.values(PortalPermission), // All permissions
  
  /**
   * PROPERTY MANAGER - Operational access to assigned properties
   * Can manage day-to-day operations but read-only on financials
   * Cannot delete properties or change ownership
   * Per PROPERTY_MANAGER_SOP: Tenants can be managed but "deleted" means archived only (soft delete)
   */
  property_manager: [
    // Properties (view/update only, no create/delete)
    PortalPermission.PROPERTY_VIEW,
    PortalPermission.PROPERTY_UPDATE,
    
    // Tenants (full access except permanent delete - soft delete/archive only)
    PortalPermission.TENANT_VIEW,
    PortalPermission.TENANT_CREATE,
    PortalPermission.TENANT_UPDATE,
    PortalPermission.TENANT_DELETE, // Soft delete (archive) via cloud function - not permanent deletion
    PortalPermission.TENANT_COMMUNICATE,
    
    // Leases (full access except delete)
    PortalPermission.LEASE_VIEW,
    PortalPermission.LEASE_CREATE,
    PortalPermission.LEASE_UPDATE,
    PortalPermission.LEASE_SIGN,
    PortalPermission.LEASE_TERMINATE,
    
    // Financials (READ ONLY)
    PortalPermission.FINANCIAL_VIEW,
    PortalPermission.PAYMENT_VIEW,
    PortalPermission.INVOICE_VIEW,
    
    // Maintenance (full access)
    PortalPermission.MAINTENANCE_VIEW,
    PortalPermission.MAINTENANCE_CREATE,
    PortalPermission.MAINTENANCE_UPDATE,
    PortalPermission.MAINTENANCE_ASSIGN,
    PortalPermission.MAINTENANCE_COMPLETE,
    PortalPermission.MAINTENANCE_DELETE,
    
    // Documents (full access)
    PortalPermission.DOCUMENT_VIEW,
    PortalPermission.DOCUMENT_UPLOAD,
    PortalPermission.DOCUMENT_DELETE,
    PortalPermission.DOCUMENT_SIGN,
    
    // Reports (view/generate only, no export)
    PortalPermission.REPORT_VIEW,
    PortalPermission.REPORT_GENERATE,
    PortalPermission.ANALYTICS_VIEW,
    
    // Team (view only)
    PortalPermission.TEAM_VIEW,
    
    // Settings (view only)
    PortalPermission.SETTINGS_VIEW,
  ],
  
  /**
   * ASSISTANT/STAFF - Limited read/write access
   * Can view financial data (read-only), cannot delete critical items
   */
  assistant: [
    // Properties (view only)
    PortalPermission.PROPERTY_VIEW,
    
    // Tenants (view/create/update, no delete)
    PortalPermission.TENANT_VIEW,
    PortalPermission.TENANT_CREATE,
    PortalPermission.TENANT_UPDATE,
    PortalPermission.TENANT_COMMUNICATE,
    
    // Leases (view only)
    PortalPermission.LEASE_VIEW,
    
    // FINANCIAL (view only)
    PortalPermission.PAYMENT_VIEW,
    PortalPermission.FINANCIAL_VIEW,
    
    // Maintenance (view/create only)
    PortalPermission.MAINTENANCE_VIEW,
    PortalPermission.MAINTENANCE_CREATE,
    
    // Documents (view/upload)
    PortalPermission.DOCUMENT_VIEW,
    PortalPermission.DOCUMENT_UPLOAD,
    
    // Reports (view only)
    PortalPermission.REPORT_VIEW,
    
    // Team (view only)
    PortalPermission.TEAM_VIEW,
  ],
  
  /**
   * MAINTENANCE COORDINATOR - Work orders only
   * Focused on maintenance requests and completion
   */
  maintenance_coordinator: [
    // Properties (view only - need to see property details)
    PortalPermission.PROPERTY_VIEW,
    
    // Tenants (view only - need to contact tenants)
    PortalPermission.TENANT_VIEW,
    PortalPermission.TENANT_COMMUNICATE,
    
    // Maintenance (full access)
    PortalPermission.MAINTENANCE_VIEW,
    PortalPermission.MAINTENANCE_CREATE,
    PortalPermission.MAINTENANCE_UPDATE,
    PortalPermission.MAINTENANCE_ASSIGN,
    PortalPermission.MAINTENANCE_COMPLETE,
    
    // Documents (view/upload - for work order photos)
    PortalPermission.DOCUMENT_VIEW,
    PortalPermission.DOCUMENT_UPLOAD,
  ],
  
  /**
   * TENANT - Self-service portal (Phase 3)
   * Can view their own lease, make payments, submit maintenance requests
   */
  tenant: [
    // Limited to own data only (enforced at data layer)
    PortalPermission.LEASE_VIEW,
    PortalPermission.PAYMENT_VIEW,
    PortalPermission.INVOICE_VIEW,
    PortalPermission.MAINTENANCE_VIEW,
    PortalPermission.MAINTENANCE_CREATE,
    PortalPermission.DOCUMENT_VIEW,
  ],
};

/**
 * Permission Checking Functions
 */

/**
 * Check if a role has a specific permission
 */
export function hasPermission(role: PortalRole, permission: PortalPermission): boolean {
  const permissions = PORTAL_ROLE_PERMISSIONS[role];
  if (!permissions) return false;
  return permissions.includes(permission);
}

/**
 * Check if a role has any of the specified permissions
 */
export function hasAnyPermission(role: PortalRole, permissions: PortalPermission[]): boolean {
  const rolePermissions = PORTAL_ROLE_PERMISSIONS[role];
  if (!rolePermissions) return false;
  return permissions.some(p => rolePermissions.includes(p));
}

/**
 * Check if a role has all of the specified permissions
 */
export function hasAllPermissions(role: PortalRole, permissions: PortalPermission[]): boolean {
  return permissions.every(p => hasPermission(role, p));
}

/**
 * Get all permissions for a role
 */
export function getPermissions(role: PortalRole): PortalPermission[] {
  return PORTAL_ROLE_PERMISSIONS[role];
}

/**
 * Role Display Information
 */
export const PORTAL_ROLE_DISPLAY: Record<PortalRole, { name: string; description: string; color: string }> = {
  property_owner: {
    name: 'Property Owner',
    description: 'Full access to all properties and features',
    color: 'purple',
  },
  property_manager: {
    name: 'Property Manager',
    description: 'Manages assigned properties with read-only financial access',
    color: 'blue',
  },
  assistant: {
    name: 'Assistant',
    description: 'Limited access for administrative tasks',
    color: 'green',
  },
  maintenance_coordinator: {
    name: 'Maintenance Coordinator',
    description: 'Work order and maintenance management only',
    color: 'orange',
  },
  tenant: {
    name: 'Tenant',
    description: 'Self-service portal for lease and payments',
    color: 'gray',
  },
};

/**
 * Get role display name
 */
export function getRoleDisplayName(role: PortalRole): string {
  return PORTAL_ROLE_DISPLAY[role].name;
}

/**
 * Get role description
 */
export function getRoleDescription(role: PortalRole): string {
  return PORTAL_ROLE_DISPLAY[role].description;
}

/**
 * Get role badge color
 */
export function getRoleBadgeColor(role: PortalRole): string {
  return PORTAL_ROLE_DISPLAY[role].color;
}

/**
 * Property Assignment Interface
 * For property managers who only access specific properties
 */
export interface PropertyAssignment {
  userId: string;
  propertyId: string;
  assignedAt: Date;
  assignedBy: string;
}

/**
 * User Profile with Role (Portal or Admin)
 * Supports both internal admin users and portal property management users
 */
export interface PortalUserProfile {
  uid: string;
  email: string;
  name?: string;
  role: UserRole; // Can be either AdminRole or PortalRole
  permissions: PortalPermission[];
  assignedProperties?: string[]; // For property_manager, assistant, maintenance_coordinator roles
  ownerId?: string; // For non-owner roles, primary owner reference (backward compatibility)
  ownerIds?: string[]; // For roles managing properties from multiple landlords
  propertyData?: Map<string, { landlordId: string }>; // Cached property-to-landlord mapping
}

/**
 * Get permissions for any user role (admin or portal)
 * Admin roles get ALL permissions
 * Portal roles get their defined permissions
 */
export function getPermissionsForRole(role: UserRole): PortalPermission[] {
  // Admin roles get all permissions
  if (isAdminRole(role)) {
    return Object.values(PortalPermission);
  }
  
  // Portal roles get their defined permissions
  return PORTAL_ROLE_PERMISSIONS[role as PortalRole] || [];
}

/**
 * Check if a role is an admin role
 */
export function isAdminRole(role: UserRole | string): role is AdminRole {
  return ['superadmin', 'admin', 'sales', 'support', 'accountant'].includes(role as string);
}

/**
 * Check if a role is a portal role
 */
export function isPortalRole(role: UserRole | string): role is PortalRole {
  return ['property_owner', 'property_manager', 'assistant', 'maintenance_coordinator', 'tenant'].includes(role as string);
}
